import type { Consent, Privacy } from '@tunein/reporting-schemas';
import {
  type ConsentStatus,
  consentStatuses,
  type SystemAdTrackingStatus,
  systemAdTrackingStatuses,
  US_PRIVACY_OPT_OUT_INDEX,
} from '../constants/reporting/privacy';
import { type ConsentJurisdiction, jurisdictions } from './constants/geoRules';

/**
 * Every field of the reported `privacy` block except `lastUpdatedAt`, which is left out when there
 * is no user decision to report so that the API client's own default stands.
 */
export type PrivacyContext = Omit<Privacy, 'lastUpdatedAt'> &
  Partial<Pick<Privacy, 'lastUpdatedAt'>>;

export interface PrivacyContextOptions {
  /** From the resolved OneTrust geolocation rule. */
  jurisdiction?: ConsentJurisdiction;
  countryCode?: string;
  stateCode?: string;
  isOptedOutOfTargeting?: boolean;
  usPrivacyString?: string;
  hasInteracted?: boolean;
  /** ISO 8601. */
  lastUpdatedAt?: string;
}

/**
 * Consent as expressed through the US Privacy string, whose third character is the user's
 * do-not-sell choice.
 */
function getUsPrivacyStatus(usPrivacyString?: string): ConsentStatus {
  switch (usPrivacyString?.[US_PRIVACY_OPT_OUT_INDEX]) {
    case 'Y':
      return consentStatuses.CONSENT_STATUS_DENIED;
    case 'N':
      return consentStatuses.CONSENT_STATUS_GRANTED;
    default:
      return consentStatuses.CONSENT_STATUS_UNSPECIFIED;
  }
}

/**
 * Consent as expressed through OneTrust's targeting cookie category, which is how every regime
 * other than US Privacy records the decision.
 */
function getTargetingStatus(isOptedOutOfTargeting?: boolean): ConsentStatus {
  if (typeof isOptedOutOfTargeting !== 'boolean') {
    return consentStatuses.CONSENT_STATUS_UNSPECIFIED;
  }

  return isOptedOutOfTargeting
    ? consentStatuses.CONSENT_STATUS_DENIED
    : consentStatuses.CONSENT_STATUS_GRANTED;
}

/**
 * Whether ad tracking is authorized, and — where it is not — whether that is the user's decision.
 *
 * The distinction is the point of this field: the consent categories carry a full set of values
 * from the first page load, so a session that never saw an answer is otherwise indistinguishable
 * from a refusal. RESTRICTED covers "we cannot determine the user's authorization", which is what
 * an untouched banner leaves us with.
 */
function getSystemAdTrackingStatus(
  isOptedOutOfTargeting: boolean | undefined,
  hasInteracted: boolean,
): SystemAdTrackingStatus {
  if (typeof isOptedOutOfTargeting !== 'boolean') {
    return systemAdTrackingStatuses.SYSTEM_AD_TRACKING_STATUS_UNSPECIFIED;
  }

  if (!isOptedOutOfTargeting) {
    return systemAdTrackingStatuses.SYSTEM_AD_TRACKING_STATUS_AUTHORIZED;
  }

  return hasInteracted
    ? systemAdTrackingStatuses.SYSTEM_AD_TRACKING_STATUS_DENIED
    : systemAdTrackingStatuses.SYSTEM_AD_TRACKING_STATUS_RESTRICTED;
}

/**
 * Builds the `privacy` block of the Unified Events common context.
 *
 * Exactly one regime is ever applicable, because OneTrust applies exactly one geolocation rule to
 * a session. The regimes that do not apply report no status rather than a default one, so that a
 * query can tell "this user is not under GDPR" from "this user is under GDPR and refused".
 *
 * `lastUpdatedAt` is omitted when the user has made no decision to report. The API client's
 * default then stands, which is the epoch — a `google.protobuf.Timestamp` has no empty value, and
 * a timestamp of "now" would have every event assert a consent update that never happened.
 */
export function buildPrivacyContext({
  jurisdiction,
  countryCode,
  stateCode,
  isOptedOutOfTargeting,
  usPrivacyString,
  hasInteracted = false,
  lastUpdatedAt,
}: PrivacyContextOptions = {}): PrivacyContext {
  const consentFor = (regime: ConsentJurisdiction): Consent => {
    if (regime !== jurisdiction) {
      return { isApplicable: false, status: consentStatuses.CONSENT_STATUS_UNSPECIFIED };
    }

    return {
      isApplicable: true,
      status:
        regime === jurisdictions.usPrivacy
          ? getUsPrivacyStatus(usPrivacyString)
          : getTargetingStatus(isOptedOutOfTargeting),
    };
  };

  return {
    gdpr: consentFor(jurisdictions.gdpr),
    globalOptIn: consentFor(jurisdictions.globalOptIn),
    globalOptOut: consentFor(jurisdictions.globalOptOut),
    usPrivacy: consentFor(jurisdictions.usPrivacy),
    systemAdTrackingStatus: getSystemAdTrackingStatus(isOptedOutOfTargeting, hasInteracted),
    // Coalesced rather than defaulted: these come from selectors that may hand us null, and a null
    // fails schema validation as a format error, which costs the whole event rather than the field.
    jurisdictionDetails: { countryCode: countryCode || '', stateCode: stateCode || '' },
    ...(lastUpdatedAt && { lastUpdatedAt }),
  };
}
